import { Box } from '@vk-hr-tek/ui/Box';
import { EmptyContentState } from '@vk-hr-tek/app-ui/EmptyContentState';
import { Link } from '@vk-hr-tek/ui/Link';
import { useInject } from '@vk-hr-tek/core/ioc';
import { useQuery } from '@vk-hr-tek/core/query';
import { CircularProgress } from '@vk-hr-tek/ui/CircularProgress';
import { InfiniteScroll } from '@vk-hr-tek/ui/InfiniteScroll';

import { useSelector, useDispatch } from '@app/hooks';

import { BellNotificationsRouter } from '../../../../types';
import { IntersectionNotificationItemSkeleton } from '../../../../segments/IntersectionNotificationItemSkeleton';
import { BellNotificationsSkeleton } from '../../../../segments/BellNotificationsSkeleton';
import { NotificationGroupItem } from '../../../../segments/NotificationGroupItem';
import {
  selectBellNotificationsStatus,
  selectBellNotificationsWithDateGroups,
  selectIsBellMarkReadAllPending,
  getBellAdditionalListNotifications,
  selectHasBellMoreNotifications,
  selectBellCurrentLastNotificationId,
  markReadOnBell,
} from '../../../../slice';
import { NOTIFICATIONS_STATUSES } from '../../../../constants';
import { NotificationsFiltersDto } from '../../../../dto';

interface BodyProps {
  onClose(): void;
}

export const Body = ({ onClose }: BodyProps) => {
  const dispatch = useDispatch();

  const status = useSelector(selectBellNotificationsStatus);
  const items = useSelector(selectBellNotificationsWithDateGroups);
  const isMarkReadLoading = useSelector(selectIsBellMarkReadAllPending);
  const hasBellMoreNotifications = useSelector(selectHasBellMoreNotifications);
  const bellCurrentLastNotificationId = useSelector(
    selectBellCurrentLastNotificationId,
  );

  const [filter] = useQuery<NotificationsFiltersDto>(NotificationsFiltersDto);

  const activeFilters = filter?.filters || {};

  const router = useInject<BellNotificationsRouter>(BellNotificationsRouter);

  const handleGoToNotificationsPage = () => {
    router.goToNotificationsPage();
    onClose();
  };

  const handleInfiniteScrollNext = () => {
    dispatch(
      getBellAdditionalListNotifications({
        limit: 15,
        isRead: false,
        ...(bellCurrentLastNotificationId && {
          lastId: bellCurrentLastNotificationId,
        }),
      }),
    );
  };

  const handleReadClick = (id: string) => {
    dispatch(
      markReadOnBell({
        id,
        removeFromList: activeFilters.status === NOTIFICATIONS_STATUSES.UNREAD,
      }),
    );

    if (hasBellMoreNotifications && id === bellCurrentLastNotificationId) {
      handleInfiniteScrollNext();
    }
  };

  if (status === 'loading') {
    return (
      <Box py="12" px="8">
        {status === 'loading' && <BellNotificationsSkeleton />}
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box
        py="32"
        px="8"
        gap="4"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <EmptyContentState title="Здесь будут ваши непрочитанные уведомления" />
        <Link stroke={false} onClick={handleGoToNotificationsPage}>
          Перейти ко всем уведомлениям
        </Link>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="12"
      maxHeight={422}
      py="12"
      px="8"
      overflow="scroll"
      id="scrollableBellMenu"
    >
      {isMarkReadLoading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          next={() => {}}
          hasMore={hasBellMoreNotifications}
          loader={
            items.length > 0 &&
            hasBellMoreNotifications && (
              <IntersectionNotificationItemSkeleton
                onIntersect={handleInfiniteScrollNext}
              />
            )
          }
          endMessage={<div />}
          scrollId="scrollableBellMenu"
        >
          <Box display="flex" flexDirection="column" gap="4">
            {items.map((notification) => (
              <NotificationGroupItem
                key={
                  notification.type === 'group'
                    ? notification.label
                    : notification.id
                }
                variant="plugin"
                data={notification}
                onReadClick={handleReadClick}
                onClose={onClose}
              />
            ))}
          </Box>
        </InfiniteScroll>
      )}
    </Box>
  );
};
